import type { FieldSchema, FormSchema, QuestionOption } from '@stepform';
import { resolveFieldOptions } from './fieldOptions';
import type { DependantFieldSchema } from './components/DependantFields';
import type { NotifiableEventsFieldSchema } from './components/NotifiableEventsField';
import type { SplitQuantityFieldSchema } from './components/SplitQuantityField';

type ExtractivesField =
  | FieldSchema
  | DependantFieldSchema
  | SplitQuantityFieldSchema
  | NotifiableEventsFieldSchema
  | { type: 'NZBN'; id: string; label?: string; optional?: boolean }
  | { type: 'Name'; id: string; label?: string; optional?: boolean; supportText?: string };

function optionsFor(
  fieldOptions: Record<string, QuestionOption[] | Record<string, QuestionOption[]>>,
  key: string
): QuestionOption[] {
  const value = fieldOptions[key];
  return Array.isArray(value) ? value : [];
}

function dependantOptionsFor(
  fieldOptions: Record<string, QuestionOption[] | Record<string, QuestionOption[]>>,
  key: string
): Record<string, QuestionOption[]> {
  const value = fieldOptions[key];
  return value && !Array.isArray(value) ? value : {};
}

export function buildSchema(
  fieldOptions: Record<string, QuestionOption[] | Record<string, QuestionOption[]>> = {}
): FormSchema {
  const options = resolveFieldOptions(fieldOptions);

  return {
    steps: [
      {
        id: 'step1',
        label: 'Step 1',
        title: 'Operation details',
        navigationLabel: 'Operation<br>details',
        sections: [
          {
            id: 'section-reporting-period',
            showTopDivider: false,
            groups: [
              {
                id: 'reporting-period',
                showTopDivider: false,
                label: 'Reporting period',
                fields: [
                  {
                    type: 'DropDown',
                    id: 'ReportingPeriod',
                    label: 'Which quarter are you reporting for?',
                    options: optionsFor(options, 'ReportingPeriod'),
                  } as FieldSchema,
                  {
                    type: 'Radio',
                    id: 'WorkersOnSite',
                    label: 'Were there any workers on-site during the reporting period?',
                    options: optionsFor(options, 'WorkersOnSite'),
                    supportText:
                      'Workers includes both your employees and other workers (for example, contractors).',
                  } as FieldSchema,
                ],
              },
            ],
          },
          {
            id: 'section-operation-details',
            showTopDivider: false,
            groups: [
              {
                id: 'operation-details',
                showTopDivider: false,
                label: 'Operation details',
                fields: [
                  { type: 'Text', id: 'OperationName', label: 'Name of mine, tunnel, quarry or alluvial mine' },
                  {
                    type: 'DropDown',
                    id: 'OperationType',
                    label: 'Type of operation',
                    options: optionsFor(options, 'OperationType'),
                  } as FieldSchema,
                  {
                    type: 'DropDownDependant',
                    id: 'PrimaryMaterial',
                    label: 'Primary material extracted during the reporting period',
                    dependsOn: 'OperationType',
                    dependantOptions: dependantOptionsFor(options, 'PrimaryMaterial'),
                  } as DependantFieldSchema,
                  { type: 'TextArea', id: 'OperationAddress', label: 'Address of operation' },
                ] as ExtractivesField[],
              },
            ],
          },
          {
            id: 'section-company-details',
            showTopDivider: false,
            groups: [
              {
                id: 'company-details',
                showTopDivider: false,
                label: 'Company details',
                fields: [
                  {
                    type: 'Text',
                    id: 'CompanyName',
                    label: 'Name of Company',
                    supportText: 'The company who operates the mine/tunnel/quarry.',
                  },
                  {
                    type: 'Text',
                    id: 'TradingName',
                    label: 'Trading name',
                    optional: true,
                    supportText: 'The public-facing/trading name, if different to legal name.',
                  },
                  { type: 'TextArea', id: 'CompanyAddress', label: 'Company address' },
                  { type: 'NZBN', id: 'NZBN', label: 'New Zealand Business Number NZBN', optional: true },
                  {
                    type: 'Text',
                    id: 'PermitNumber',
                    label: 'Permit number (if applicable)',
                    optional: true,
                  },
                ] as ExtractivesField[],
              },
            ],
          },
        ],
      },
      {
        id: 'step2',
        label: 'Step 2',
        title: 'Contact details',
        navigationLabel: 'Contact<br>details',
        sections: [
          {
            id: 'section-contact-details',
            showTopDivider: false,
            groups: [
              {
                id: 'manager-details',
                showTopDivider: false,
                label: 'Mine/Tunnel/Quarry Manager details',
                fields: [
                  { type: 'Name', id: 'ManagerName', label: 'Manager name' },
                  { type: 'Email', id: 'ManagerWorkEmail', label: 'Manager work email' },
                  {
                    type: 'Phone',
                    id: 'ManagerWorkPhone',
                    label: 'Manager primary work phone',
                    supportText: 'Mobile number preferred.',
                  },
                  {
                    type: 'DropDown',
                    id: 'ManagerCertOfCompetence',
                    label: 'Relevant Manager Certificate of Competence',
                    supportText:
                      'This is the Certificate of Competence the Manager has for their current role.',
                    options: optionsFor(options, 'ManagerCertOfCompetence'),
                  } as FieldSchema,
                ] as ExtractivesField[],
              },
              {
                id: 'site-senior-executive-details',
                showTopDivider: false,
                label: 'Site Senior Executive details',
                fields: [
                  { type: 'Name', id: 'SiteSeniorExecutiveName', label: 'Site Senior Executive name' },
                  {
                    type: 'Email',
                    id: 'SiteSeniorExecutiveWorkEmail',
                    label: 'Site Senior Executive work email',
                  },
                  {
                    type: 'Phone',
                    id: 'SiteSeniorExecutiveWorkPhone',
                    label: 'Site Senior Executive primary work phone',
                    supportText: 'Mobile number preferred.',
                  },
                ] as ExtractivesField[],
              },
              {
                id: 'submitter-role',
                showTopDivider: false,
                label: 'Person submitting report',
                fields: [
                  {
                    type: 'RadioDependant',
                    id: 'SubmitterRole',
                    label: 'Who is submitting this report?',
                    dependsOn: 'OperationType',
                    dependantOptions: dependantOptionsFor(options, 'SubmitterRole'),
                  } as DependantFieldSchema,
                ],
              },
              {
                id: 'submitter-details',
                showTopDivider: false,
                fields: [
                  { type: 'Name', id: 'SubmitterName', label: 'Your name' },
                  { type: 'Email', id: 'SubmitterWorkEmail', label: 'Your work email' },
                  {
                    type: 'Phone',
                    id: 'SubmitterWorkPhone',
                    label: 'Your primary work phone',
                    supportText: 'Mobile number preferred.',
                  },
                ] as ExtractivesField[],
              },
            ],
          },
        ],
      },
      {
        id: 'step3',
        label: 'Step 3',
        title: 'Notifiable events',
        navigationLabel: 'Notifiable<br>events',
        sections: [
          {
            id: 'section-workers-notifiable-events',
            showTopDivider: false,
            label: 'Workers and notifiable events',
            groups: [
              {
                id: 'workers-notifiable-events',
                label: 'Workers',
                showTopDivider: false,
                fields: [
                  {
                    type: 'SplitQuantity',
                    id: 'OperationNumbers',
                    label: 'Number of workers at the operation',
                    supportText:
                      'The average number of workers at the operation each day during the reporting period.',
                    min: 0,
                    minTotal: 1,
                  },
                  {
                    type: 'SplitQuantity',
                    id: 'OperationHours',
                    label: 'Number of hours worked',
                    supportText:
                      'The total number of hours worked at the operation by all workers during the reporting period (including additional shift and overtime hours). ',
                    min: 0,
                    minTotal: 1,
                  },
                ] as ExtractivesField[],
              },
              {
                id: 'notifiable-events',
                label: 'Notifiable events',
                showTopDivider: false,
                fields: [
                  {
                    type: 'Radio',
                    id: 'NotifiableEvents',
                    label: 'Were there any notifiable events during the reporting period?',
                    options: optionsFor(options, 'NotifiableEvents'),
                  } as FieldSchema,
                  {
                    type: 'NotifiableEvents',
                    id: 'CombinedNotifiableEvents',
                    label: 'Number of notifiable events',
                    supportText:
                      'The total number of notifiable events that occurred during the reporting period.',
                    min: 0,
                    minTotal: 1,
                  },
                ] as ExtractivesField[],
              },
            ],
          },
        ],
      },
      {
        id: 'step4',
        label: 'Step 4',
        title: 'Injuries & illnesses',
        navigationLabel: 'Injuries &<br>illnesses',
        sections: [
          {
            id: 'section-notifiable-injuries-illnesses',
            showTopDivider: false,
            label: 'Injuries & illnesses',
            groups: [
              {
                id: 'notifiable-injuries-illnesses',
                label: 'Notifiable injuries or illnesses',
                showTopDivider: false,
                fields: [
                  {
                    type: 'Radio',
                    id: 'Injuries',
                    label: 'Were there any notifiable injuries or illnesses during the reporting period?',
                    supportText:
                      'These are injuries or illnesses that occurred as a result of a notifiable event.',
                    options: optionsFor(options, 'Injuries'),
                  } as FieldSchema,
                ],
              },
              {
                id: 'types-of-injuries-illnesses',
                label: 'Types of injuries or illnesses',
                showTopDivider: false,
                fields: [
                  {
                    type: 'SplitQuantity',
                    id: 'InjuriesTime',
                    label: 'Number of lost-time injuries or illnesses',
                    supportText:
                      'The total number of injuries or illnesses to workers, as a result of a notifiable event, that resulted in those workers being unable to work for 1 day or more (not including the day of the event) during the reporting period. This applies whether the worker was rostered on that day or not.',
                    min: 0,
                  },
                  {
                    type: 'SplitQuantity',
                    id: 'InjuriesAltDuty',
                    label: 'Number of alternative duty injuries or illnesses',
                    supportText:
                      'The total number of illnesses or injuries to workers, as a result of a notifiable event, that resulted in those workers being on alternative duties during the reporting period, <strong>but</strong> did not result in any days lost from work (not including the day of the event).',
                    min: 0,
                  },
                  {
                    type: 'SplitQuantity',
                    id: 'InjuriesMedTreatment',
                    label: 'Number of medical treatment injuries',
                    supportText:
                      'The total number of injuries to workers, that occurred while the worker was working at the operation, and needed medical treatment during the reporting period, <strong>but</strong> did not require a day lost from work or alternative duties (other than the day of the event).<br><br><strong>Note:</strong> Medical treatment here means the management or care of a patient. It does not include diagnostic procedures (like tests or examinations), observation, counselling, first aid or therapeutic measures taken to prevent injury or illness in the future.',
                    min: 0,
                  },
                ] as ExtractivesField[],
              },
            ],
          },
        ],
      },
      {
        id: 'step5',
        label: 'Step 5',
        title: 'Days lost & alternative duties',
        navigationLabel: 'Days lost &<br>alternative duties',
        sections: [
          {
            id: 'section-days-lost-alternative-duties',
            showTopDivider: false,
            label: 'Days lost from work and alternative duties days',
            groups: [
              {
                id: 'days-lost-alternative-duties',
                showTopDivider: false,
                fields: [
                  {
                    type: 'SplitQuantity',
                    id: 'DaysLost',
                    label: 'Total number of days lost from work (for all injuries and illnesses)',
                    supportText:
                      'The total number of days workers were unable to work, as a result of a notifiable injury or illness. Do not include the day of the event.<br><br><strong>Note:</strong><ul><li>Lost days should be counted even if the injury happened or illness began in a previous reporting period.</li><li>Lost days should be counted whether the worker was rostered on or not.</li></ul>',
                    min: 0,
                  },
                  {
                    type: 'SplitQuantity',
                    id: 'DaysAltDuty',
                    label: 'Total number of alternative duty days (for all injuries and illnesses)',
                    supportText:
                      'The total number of days workers were on alternative duties, as a result of a notifiable injury or illness. Do not include the day of the event.',
                    min: 0,
                  },
                ] as ExtractivesField[],
              },
            ],
          },
        ],
      },
      {
        id: 'step6',
        label: 'Step 6',
        title: 'Fatalities',
        navigationLabel: 'Fatalities',
        sections: [
          {
            id: 'section-fatalities',
            showTopDivider: false,
            label: 'Fatalities',
            groups: [
              {
                id: 'fatalities',
                showTopDivider: false,
                fields: [
                  {
                    type: 'Radio',
                    id: 'Fatalities',
                    label: 'Were there any fatalities during the reporting period?',
                    options: optionsFor(options, 'Fatalities'),
                  } as FieldSchema,
                  {
                    type: 'SplitQuantity',
                    id: 'CombinedFatalities',
                    label: 'Total number of fatalities',
                    supportText:
                      'The total number of fatalities that occurred during the reporting period.',
                    min: 0,
                    minTotal: 1,
                  },
                ] as ExtractivesField[],
              },
            ],
          },
        ],
      },
      {
        id: 'step7',
        label: 'Step 7',
        title: 'Review',
        navigationLabel: 'Review',
        component: 'ExtractivesReview',
        sections: [
          {
            id: 'section-review',
            showTopDivider: false,
            label: 'Review your information',
            groups: [
              {
                id: 'comment',
                fields: [
                  {
                    type: 'TextArea',
                    id: 'Comment',
                    label: 'Leave a comment',
                    optional: true,
                    supportText:
                      'Please let us know if something was not clear or if you were not sure how to complete any sections.',
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };
}
